import type { MessageKey } from "@expertloop/i18n";
import { useState, useEffect } from "react";
import { adminApi, AdminApiError } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import type { AdminUser, AdminPermission } from "@expertloop/shared";
import { useI18n } from "@expertloop/i18n/react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useConfirmedEmailAction } from "../features/account-access/useConfirmedEmailAction";

interface AdminsResponse {
	admins: Omit<AdminUser, "passwordHash">[];
}

interface CreateAdminForm {
	email: string;
	password: string;
	firstName: string;
	lastName: string;
	role: "admin" | "super_admin";
	permissions: AdminPermission[];
}

interface EditAdminForm {
	firstName: string;
	lastName: string;
	role: "admin" | "super_admin";
	permissions: AdminPermission[];
}

interface PermissionGroup {
	nameKey: MessageKey;
	permissions: { value: AdminPermission; labelKey: MessageKey; descriptionKey: MessageKey }[];
}

const PERMISSION_GROUPS: PermissionGroup[] = [
	{
		nameKey: "adminAdmins.groupUsersChats",
		permissions: [
			{ value: "view_users", labelKey: "adminAdmins.permViewUsers", descriptionKey: "adminAdmins.permViewUsersDesc" },
			{ value: "manage_users", labelKey: "adminAdmins.permManageUsers", descriptionKey: "adminAdmins.permManageUsersDesc" },
			{ value: "view_chats", labelKey: "adminAdmins.permViewChats", descriptionKey: "adminAdmins.permViewChatsDesc" },
			{ value: "view_orders", labelKey: "adminAdmins.permViewOrders", descriptionKey: "adminAdmins.permViewOrdersDesc" },
			{ value: "impersonate_users", labelKey: "adminAdmins.permImpersonateUsers", descriptionKey: "adminAdmins.permImpersonateUsersDesc" },
		],
	},
	{
		nameKey: "adminAdmins.groupMediations",
		permissions: [
			{ value: "view_mediations", labelKey: "adminAdmins.permViewMediations", descriptionKey: "adminAdmins.permViewMediationsDesc" },
			{ value: "resolve_mediations", labelKey: "adminAdmins.permResolveMediations", descriptionKey: "adminAdmins.permResolveMediationsDesc" },
		],
	},
	{
		nameKey: "adminAdmins.groupAdministration",
		permissions: [
			{ value: "manage_admins", labelKey: "adminAdmins.permManageAdmins", descriptionKey: "adminAdmins.permManageAdminsDesc" },
		],
	},
];

export function AdminsPage() {
	const { t } = useI18n();
	const currentAdminId = useAuthStore((s) => s.admin?.id);
	const [admins, setAdmins] = useState<Omit<AdminUser, "passwordHash">[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [createForm, setCreateForm] = useState<CreateAdminForm>({
		email: "",
		password: "",
		firstName: "",
		lastName: "",
		role: "admin",
		permissions: ["view_mediations", "view_chats", "view_users"],
	});
	const [createError, setCreateError] = useState("");
	const [creating, setCreating] = useState(false);

	// Edit modal state
	const [editingAdmin, setEditingAdmin] = useState<Omit<AdminUser, "passwordHash"> | null>(null);
	const [editForm, setEditForm] = useState<EditAdminForm>({
		firstName: "",
		lastName: "",
		role: "admin",
		permissions: [],
	});
	const [editError, setEditError] = useState("");
	const [saving, setSaving] = useState(false);

	// Envoi d'un lien de réinitialisation : le lien part par email vers le compte
	// visé, le super-administrateur ne le voit jamais
	const resetLink = useConfirmedEmailAction();
	const [resetTarget, setResetTarget] = useState<Omit<AdminUser, "passwordHash"> | null>(null);

	const fetchAdmins = async () => {
		setLoading(true);
		try {
			const data = await adminApi.get<AdminsResponse>("/admin/admins");
			setAdmins(data.admins);
		} catch (err) {
			setError(err instanceof Error ? err.message : t("adminAdmins.loadFailed"));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchAdmins();
	}, []);

	const handleCreate = async (e: React.FormEvent) => {
		e.preventDefault();
		setCreateError("");
		setCreating(true);

		try {
			await adminApi.post("/admin/admins", createForm);
			setShowCreateModal(false);
			setCreateForm({
				email: "",
				password: "",
				firstName: "",
				lastName: "",
				role: "admin",
				permissions: ["view_mediations", "view_chats", "view_users"],
			});
			fetchAdmins();
		} catch (err) {
			setCreateError(err instanceof Error ? err.message : t("adminAdmins.createFailed"));
		} finally {
			setCreating(false);
		}
	};

	const isOwnAccount = (adminId: string) => adminId === currentAdminId;

	// Le refus du serveur pour son propre compte porte un code : on l'affiche traduit
	const describeUpdateError = (err: unknown): string => {
		if (err instanceof AdminApiError && err.code === "admin_self_role_or_disable") {
			return t("adminAdmins.cannotChangeOwnRole");
		}
		return err instanceof Error ? err.message : t("adminAdmins.updateFailed");
	};

	const handleEdit = (admin: Omit<AdminUser, "passwordHash">) => {
		setEditingAdmin(admin);
		setEditForm({
			firstName: admin.firstName,
			lastName: admin.lastName,
			role: admin.role,
			permissions: [...admin.permissions],
		});
		setEditError("");
	};

	const handleSaveEdit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!editingAdmin) return;

		setEditError("");
		setSaving(true);

		try {
			// Son propre rôle ne se change pas : on ne l'envoie pas, le serveur le refuserait
			const { role, ...withoutRole } = editForm;
			await adminApi.put(`/admin/admins/${editingAdmin.id}`, isOwnAccount(editingAdmin.id) ? withoutRole : { ...withoutRole, role });
			setEditingAdmin(null);
			fetchAdmins();
		} catch (err) {
			setEditError(describeUpdateError(err));
		} finally {
			setSaving(false);
		}
	};

	const handleToggleDisabled = async (adminId: string, currentlyDisabled: boolean) => {
		try {
			await adminApi.put(`/admin/admins/${adminId}`, {
				disabled: !currentlyDisabled,
			});
			fetchAdmins();
		} catch (err) {
			setError(describeUpdateError(err));
		}
	};

	const handleAskResetLink = (admin: Omit<AdminUser, "passwordHash">) => {
		setResetTarget(admin);
		resetLink.ask();
	};

	const handleSendResetLink = () => {
		if (!resetTarget) return;
		return resetLink.confirm(async () => {
			await adminApi.post(`/admin/admins/${resetTarget.id}/password-reset`);
			return t("adminAdmins.resetLinkSent", { email: resetTarget.email });
		}, () => t("adminAdmins.resetLinkFailed"));
	};

	const toggleCreatePermission = (permission: AdminPermission) => {
		setCreateForm((prev) => ({
			...prev,
			permissions: prev.permissions.includes(permission)
				? prev.permissions.filter((p) => p !== permission)
				: [...prev.permissions, permission],
		}));
	};

	const toggleEditPermission = (permission: AdminPermission) => {
		setEditForm((prev) => ({
			...prev,
			permissions: prev.permissions.includes(permission)
				? prev.permissions.filter((p) => p !== permission)
				: [...prev.permissions, permission],
		}));
	};

	const formatDate = (date?: Date | string) => {
		if (!date) return t('adminAdmins.never');
		return new Date(date).toLocaleString();
	};

	const renderPermissionsSelector = (
		selectedPermissions: AdminPermission[],
		onToggle: (permission: AdminPermission) => void,
		disabled?: boolean
	) => (
		<div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
			{PERMISSION_GROUPS.map((group) => (
				<div key={group.nameKey}>
					<div
						style={{
							fontSize: "0.6875rem",
							fontWeight: 600,
							color: "var(--color-gray-400)",
							textTransform: "uppercase",
							letterSpacing: "0.05em",
							marginBottom: "0.625rem",
						}}
					>
						{t(group.nameKey)}
					</div>
					<div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
						{group.permissions.map((perm) => {
							const isSelected = selectedPermissions.includes(perm.value);
							const isSensitive = perm.value === "impersonate_users" || perm.value === "manage_admins";
							return (
								<label
									key={perm.value}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.75rem",
										padding: "0.5rem 0.75rem",
										borderRadius: "0.375rem",
										background: isSelected ? "var(--color-gray-50)" : "transparent",
										cursor: disabled ? "not-allowed" : "pointer",
										opacity: disabled ? 0.6 : 1,
									}}
								>
									<input
										type="checkbox"
										checked={isSelected}
										onChange={() => onToggle(perm.value)}
										disabled={disabled}
										style={{ width: "1rem", height: "1rem", flexShrink: 0 }}
									/>
									<div style={{ flex: 1 }}>
										<span style={{ fontSize: "0.875rem", fontWeight: 500 }}>
											{t(perm.labelKey)}
										</span>
										{isSensitive && (
											<span
												style={{
													fontSize: "0.625rem",
													background: "rgba(202, 138, 4, 0.15)",
													color: "var(--color-warning)",
													padding: "0.125rem 0.375rem",
													borderRadius: "0.25rem",
													fontWeight: 600,
													marginLeft: "0.5rem",
													verticalAlign: "middle",
												}}
											>
												{t('adminAdmins.sensitive')}
											</span>
										)}
										<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
											{t(perm.descriptionKey)}
										</div>
									</div>
								</label>
							);
						})}
					</div>
				</div>
			))}
		</div>
	);

	return (
		<div>
			<div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
				<div>
					<h1>{t('adminAdmins.title')}</h1>
					<p>{t('adminAdmins.accountsCount', { count: admins.length })}</p>
				</div>
				<button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
					{t('adminAdmins.createAdmin')}
				</button>
			</div>

			{error && <div className="error-message">{error}</div>}
			{resetLink.notice && (
				<div className={resetLink.notice.kind === "success" ? "notice-success" : "error-message"} role="status">
					{resetLink.notice.text}
				</div>
			)}

			<div className="card">
				{loading ? (
					<div className="loading">{t('adminAdmins.loading')}</div>
				) : admins.length === 0 ? (
					<div className="empty-state">
						<h3>{t('adminAdmins.emptyTitle')}</h3>
						<p>{t('adminAdmins.emptyBody')}</p>
					</div>
				) : (
					<div className="table-container">
						<table>
							<thead>
								<tr>
									<th>{t('adminAdmins.admin')}</th>
									<th>{t('adminUsersPage.role')}</th>
									<th>{t('adminAdmins.permissions')}</th>
									<th>{t('adminAdmins.twoFactor')}</th>
									<th>{t('adminAdmins.lastLogin')}</th>
									<th>{t('adminChatsPage.status')}</th>
									<th>{t('adminChatsPage.actions')}</th>
								</tr>
							</thead>
							<tbody>
								{admins.map((admin) => (
									<tr key={admin.id}>
										<td>
											<div style={{ fontWeight: 500 }}>
												{admin.firstName} {admin.lastName}
											</div>
											<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
												{admin.email}
											</div>
										</td>
										<td>
											<span
												className={`status-badge ${
													admin.role === "super_admin" ? "accepted" : "pending"
												}`}
											>
												{admin.role.replace("_", " ")}
											</span>
										</td>
										<td>
											<div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
												{admin.role === "super_admin" ? (
													<span style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
														{t('adminAdmins.allPermissions')}
													</span>
												) : (
													admin.permissions.slice(0, 3).map((p) => (
														<span
															key={p}
															style={{
																fontSize: "0.625rem",
																background: "var(--color-gray-100)",
																padding: "0.125rem 0.375rem",
																borderRadius: "0.25rem",
															}}
														>
															{p.replace("_", " ")}
														</span>
													))
												)}
												{admin.role !== "super_admin" && admin.permissions.length > 3 && (
													<span style={{ fontSize: "0.625rem", color: "var(--color-gray-500)" }}>
														{t('adminAdmins.morePermissions', { count: admin.permissions.length - 3 })}
													</span>
												)}
											</div>
										</td>
										<td>
											<span
												className={`status-badge ${admin.twoFactorEnabled ? "completed" : "pending"}`}
											>
												{admin.twoFactorEnabled ? t('adminAdmins.twoFactorOn') : t('adminAdmins.twoFactorOff')}
											</span>
										</td>
										<td style={{ fontSize: "0.875rem" }}>{formatDate(admin.lastLoginAt)}</td>
										<td>
											<span
												className={`status-badge ${admin.disabled ? "declined" : "completed"}`}
											>
												{admin.disabled ? t('adminAdmins.disabled') : t('productsPage.active')}
											</span>
										</td>
										<td>
											<div style={{ display: "flex", gap: "0.5rem" }}>
												<button
													className="btn btn-outline btn-sm"
													onClick={() => handleEdit(admin)}
												>
													{t('productsPage.editShort')}
												</button>
												<button
													className="btn btn-outline btn-sm"
													onClick={() => handleToggleDisabled(admin.id, !!admin.disabled)}
													disabled={isOwnAccount(admin.id)}
													title={isOwnAccount(admin.id) ? t('adminAdmins.cannotChangeOwnRole') : undefined}
												>
													{admin.disabled ? t('adminAdmins.enable') : t('adminAdmins.disable')}
												</button>
												{!admin.disabled && (
													<button
														className="btn btn-outline btn-sm"
														onClick={() => handleAskResetLink(admin)}
													>
														{t('adminAdmins.sendResetLink')}
													</button>
												)}
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{resetLink.isConfirming && resetTarget && (
				<ConfirmDialog
					title={t('adminAdmins.resetLinkTitle')}
					message={t('adminAdmins.resetLinkBody', { email: resetTarget.email })}
					confirmLabel={t('adminAdmins.resetLinkSend')}
					cancelLabel={t('common.cancel')}
					onConfirm={handleSendResetLink}
					onCancel={resetLink.cancel}
				/>
			)}

			{/* Create Modal */}
			{showCreateModal && (
				<div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
					<div
						className="modal-content"
						style={{ maxWidth: "600px" }}
						onClick={(e) => e.stopPropagation()}
					>
						<h2>{t('adminAdmins.createAdminUser')}</h2>

						{createError && <div className="error-message">{createError}</div>}

						<form onSubmit={handleCreate}>
							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
								<div className="form-group">
									<label>{t('auth.register.firstName')}</label>
									<input
										type="text"
										value={createForm.firstName}
										onChange={(e) =>
											setCreateForm((prev) => ({ ...prev, firstName: e.target.value }))
										}
										required
									/>
								</div>
								<div className="form-group">
									<label>{t('auth.register.lastName')}</label>
									<input
										type="text"
										value={createForm.lastName}
										onChange={(e) =>
											setCreateForm((prev) => ({ ...prev, lastName: e.target.value }))
										}
										required
									/>
								</div>
							</div>

							<div className="form-group">
								<label>{t('auth.login.email')}</label>
								<input
									type="email"
									value={createForm.email}
									onChange={(e) =>
										setCreateForm((prev) => ({ ...prev, email: e.target.value }))
									}
									required
								/>
							</div>

							<div className="form-group">
								<label>{t('auth.login.password')}</label>
								<input
									type="password"
									value={createForm.password}
									onChange={(e) =>
										setCreateForm((prev) => ({ ...prev, password: e.target.value }))
									}
									minLength={8}
									required
								/>
							</div>

							<div className="form-group">
								<label>{t('adminUsersPage.role')}</label>
								<select
									className="filter-select"
									style={{ width: "100%" }}
									value={createForm.role}
									onChange={(e) =>
										setCreateForm((prev) => ({
											...prev,
											role: e.target.value as "admin" | "super_admin",
										}))
									}
								>
									<option value="admin">{t('adminAdmins.admin')}</option>
									<option value="super_admin">{t('adminAdmins.superAdmin')}</option>
								</select>
							</div>

							{createForm.role === "admin" && (
								<div className="form-group">
									<label style={{ marginBottom: "0.75rem", display: "block" }}>{t('adminAdmins.permissions')}</label>
									{renderPermissionsSelector(createForm.permissions, toggleCreatePermission)}
								</div>
							)}

							<div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
								<button type="submit" className="btn btn-primary" disabled={creating}>
									{creating ? t('adminAdmins.creating') : t('adminAdmins.createAdmin')}
								</button>
								<button
									type="button"
									className="btn btn-outline"
									onClick={() => setShowCreateModal(false)}
								>
									{t('common.cancel')}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{/* Edit Modal */}
			{editingAdmin && (
				<div className="modal-overlay" onClick={() => setEditingAdmin(null)}>
					<div
						className="modal-content"
						style={{ maxWidth: "600px" }}
						onClick={(e) => e.stopPropagation()}
					>
						<h2>{t('adminAdmins.editAdmin', { name: `${editingAdmin.firstName} ${editingAdmin.lastName}` })}</h2>
						<p style={{ color: "var(--color-gray-500)", marginBottom: "1.5rem" }}>
							{editingAdmin.email}
						</p>

						{editError && <div className="error-message">{editError}</div>}

						<form onSubmit={handleSaveEdit}>
							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
								<div className="form-group">
									<label>{t('auth.register.firstName')}</label>
									<input
										type="text"
										value={editForm.firstName}
										onChange={(e) =>
											setEditForm((prev) => ({ ...prev, firstName: e.target.value }))
										}
										required
									/>
								</div>
								<div className="form-group">
									<label>{t('auth.register.lastName')}</label>
									<input
										type="text"
										value={editForm.lastName}
										onChange={(e) =>
											setEditForm((prev) => ({ ...prev, lastName: e.target.value }))
										}
										required
									/>
								</div>
							</div>

							<div className="form-group">
								<label>{t('adminUsersPage.role')}</label>
								<select
									className="filter-select"
									style={{ width: "100%" }}
									value={editForm.role}
									disabled={isOwnAccount(editingAdmin.id)}
									onChange={(e) =>
										setEditForm((prev) => ({
											...prev,
											role: e.target.value as "admin" | "super_admin",
										}))
									}
								>
									<option value="admin">{t('adminAdmins.admin')}</option>
									<option value="super_admin">{t('adminAdmins.superAdmin')}</option>
								</select>
								{isOwnAccount(editingAdmin.id) && (
									<p style={{ fontSize: "0.75rem", color: "var(--color-gray-500)", marginTop: "0.25rem" }}>
										{t('adminAdmins.ownRoleLocked')}
									</p>
								)}
							</div>

							{editForm.role === "admin" && (
								<div className="form-group">
									<label style={{ marginBottom: "0.75rem", display: "block" }}>{t('adminAdmins.permissions')}</label>
									{renderPermissionsSelector(editForm.permissions, toggleEditPermission)}
								</div>
							)}

							{editForm.role === "super_admin" && (
								<div
									style={{
										padding: "1rem",
										background: "var(--color-gray-50)",
										borderRadius: "0.5rem",
										marginBottom: "1rem",
									}}
								>
									<p style={{ color: "var(--color-gray-600)", fontSize: "0.875rem" }}>
										{t('adminAdmins.superAdminNote')}
									</p>
								</div>
							)}

							<div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
								<button type="submit" className="btn btn-primary" disabled={saving}>
									{saving ? t('profile.saving') : t('adminAdmins.saveChanges')}
								</button>
								<button
									type="button"
									className="btn btn-outline"
									onClick={() => setEditingAdmin(null)}
								>
									{t('common.cancel')}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}
